import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'removeAppFilters'
	| 'filteredAppsTooltip'
	| 'ghostIconsConnectAppsTooltip'
	| 'filteredAppsLabel'
	| 'ghostIconsLabel'
	| 'newBadge'
	| 'agentDisabledTooltip'
	| 'agentSourceControlSpotlightTitle'
	| 'agentSourceControlSpotlightBody'
	| 'agentSourceControlSpotlightUnderstood';

export const messages: Record<
	MessageKeys,
	{ id: string; defaultMessage: string; description?: string }
> = defineMessages({
	removeAppFilters: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.remove-app-filters',
		defaultMessage: 'Remove all app filters',
		description:
			'Button label or action text displayed on a pill/badge component in the chat input area that allows users to remove application-specific filters from the conversation. Shown as clickable text on the filtered apps indicator pill. When clicked, it removes all app filters and returns to searching across all available sources. The purpose is to provide a clear way for users to clear source filters and expand the search scope.',
	},
	filteredAppsTooltip: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.tooltip',
		defaultMessage: 'Filtered sources mode',
		description:
			'Tooltip text displayed when hovering over the filtered apps pill/badge in the chat input area. Shown as a tooltip to provide additional context about filtered sources mode. The purpose is to help users understand that the conversation is limited to specific applications or data sources.',
	},
	ghostIconsConnectAppsTooltip: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.ghost-icons-connect-apps-tooltip',
		defaultMessage: 'Connect apps so Rovo can reference the work you do',
		description:
			'Tooltip text displayed when ghost app icons are shown in the filtered apps pill. It nudges users to connect third-party apps so Rovo can reference their work.',
	},
	filteredAppsLabel: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.label',
		defaultMessage: 'Sources:',
		description:
			'Label text displayed before the list of filtered application sources in the chat input area. Shown as a prefix label on the filtered apps pill/badge, followed by the names of the selected sources. The purpose is to introduce and identify the list of filtered data sources that are currently active in the conversation.',
	},
	ghostIconsLabel: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.ghost-icons-label',
		defaultMessage: 'Suggested apps to connect',
		description:
			'Accessibility label (aria-label) for the row of suggested third-party app icons shown next to the filtered sources in the chat input area. Read out by screen readers to describe the group of unconnected apps that the user is being nudged to connect.',
	},
	newBadge: {
		id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.chat-mode-menu.filtered-apps.newBadge',
		defaultMessage: 'New',
		description:
			'Promotional label shown in the sources pill when specific knowledge is enabled for a Rovo Space',
	},
	agentDisabledTooltip: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.agent-disabled-tooltip',
		defaultMessage: 'Filtered sources are only available when chatting with Rovo.',
		description:
			'Tooltip shown on the filtered sources pill when it is disabled because the user is chatting with an agent. It explains that filtered sources are available in regular Rovo Chat.',
	},
	agentSourceControlSpotlightTitle: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.agent-source-control-spotlight-title',
		defaultMessage: 'Source control isn’t available',
		description:
			'Headline text displayed in the source-control spotlight shown after selecting an agent for the first time.',
	},
	agentSourceControlSpotlightBody: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.agent-source-control-spotlight-body',
		defaultMessage:
			'The agent does not support source control features, such as web access or content source switching.',
		description:
			'Body text displayed in the source-control spotlight shown after selecting an agent for the first time.',
	},
	agentSourceControlSpotlightUnderstood: {
		id: 'rovo.conversation-assistant.chat-prompt-input.filtered-apps.agent-source-control-spotlight-understood',
		defaultMessage: 'Understood',
		description: 'Primary action button label used to dismiss the source-control spotlight.',
	},
});
